/**
 * What a practice session asks the backend for, and what it keeps of the
 * answer — ROUTE_INVENTORY §2.11 #43.
 *
 * Both halves are invisible. The request half decides a cache key and a set of
 * query params; the response half drops concepts the runner must never show.
 * A runner rendering the wrong deck looks exactly like a runner rendering the
 * right one — the cards are real either way — so neither half can be pinned by
 * looking at the screen.
 *
 * Pure, and in `src/lib`: no hooks, no React.
 */

import type { MindspaceConcept } from '@/types/api';
import type {
  PracticeMode,
  PracticeScope,
  SessionConfig,
} from './session-types';

/** Quiz caps itself; flashcards stream fast enough not to need one. */
export const DEFAULT_QUIZ_LENGTH = 20;

/**
 * Scopes meaning "drill this whole collection" rather than "review what is
 * due". They include not-currently-due concepts so the session matches the
 * collection's full size — and therefore the count the starter card promised.
 */
export const COLLECTION_SCOPES: ReadonlySet<PracticeScope> =
  new Set<PracticeScope>(['custom', 'recent', 'starred']);

export interface QueueArgs {
  domain?: string[];
  user?: string[];
  lessons?: string[];
  limit?: number;
  questionsPerConcept: number;
  conceptIds?: string[];
  bookmarked?: boolean;
  includeNotDue: boolean;
  fresh?: boolean;
}

/**
 * The query the runner issues for its deck.
 *
 * `questionsPerConcept` is the load-bearing field and it is NOT a performance
 * knob: flashcards are concept-recall cards (name → description) and consume
 * no generated questions at all. Asking for them surfaced true/false quiz
 * prompts on the card face. It is also part of the cache key, which is what
 * stops a flashcard prefetch from satisfying a quiz request — the two would
 * otherwise share a deck and the quiz would render cards with no questions.
 */
export function buildQueueArgs(
  config: SessionConfig,
  mode: PracticeMode
): QueueArgs {
  const limit =
    config.length === 'all'
      ? undefined
      : (config.length ?? (mode === 'quiz' ? DEFAULT_QUIZ_LENGTH : undefined));

  return {
    domain: config.domain,
    user: config.user,
    lessons: config.lessons,
    limit,
    questionsPerConcept: mode === 'flashcard' ? 0 : 2,
    conceptIds: config.conceptIds,
    bookmarked: config.scope === 'starred' ? true : undefined,
    includeNotDue: COLLECTION_SCOPES.has(config.scope),
    fresh: config.fresh,
  };
}

export interface DeckFilterOptions {
  mode: PracticeMode;
  scope: PracticeScope;
  conceptIds?: string[];
  limit?: number;
}

/**
 * Trim the fetched queue down to what the runner may actually show.
 *
 * Three filters, in order, and the order matters — the weak narrowing applies
 * its `limit` last, so it caps what survived rather than what arrived:
 *
 * 1. **Quiz drops unanswerable questions.** Graded by exact option match,
 *    never free text, so a question with fewer than two options cannot be
 *    answered; a concept left with none is dropped entirely rather than
 *    rendered as a blank card.
 * 2. **An explicit concept allow-list wins**, whatever the mode or scope.
 *    "Practice these again" names its concepts and must get those and no
 *    others, so a non-empty allow-list ends filtering here — step 3 is skipped.
 * 3. **Flashcard `weak` narrows further than the backend does.** The server's
 *    bucketing is broad — anything not strong — and this cuts it to the signal
 *    that matters for review: recently got it wrong, or visibly struggling.
 */
export function filterSessionConcepts(
  raw: MindspaceConcept[],
  { mode, scope, conceptIds, limit }: DeckFilterOptions
): MindspaceConcept[] {
  let pool = raw;

  if (mode === 'quiz') {
    pool = pool
      .map(c => ({
        ...c,
        questions: (c.questions ?? []).filter(
          q => (q.options?.length ?? 0) >= 2
        ),
      }))
      .filter(c => c.questions.length > 0);
  }

  if (conceptIds?.length) {
    // A named deck is final: the weak narrowing (and its cap) below would
    // otherwise drop named concepts whose sr_state looks healthy.
    const wanted = new Set(conceptIds);
    return pool.filter(c => wanted.has(c.concept_id));
  }

  if (mode === 'flashcard' && scope === 'weak') {
    pool = pool
      .filter(
        c =>
          (c.sr_state.last_grade ?? 3) <= 2 || c.sr_state.struggle_score > 0.5
      )
      .slice(0, limit);
  }

  return pool;
}
